<template>
  <transition
    enter-active-class="transition duration-100 ease-in"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-100 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <overlay v-show="isOpen" @overlay-click="close">
      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="animate-fadeOut"
      >
        <card-component
          v-show="isOpen"
          data-test="upgrade-modal"
          :data-offer-type="offerType"
          :title="modalTitle"
          class="shadow-theme-dialog w-full max-h-modal md:w-3/5 lg:w-2/5 z-50"
          :header-icon="mdiClose"
          @header-icon-click="close"
        >
          <div class="space-y-4">
            <p class="text-base">{{ modalMessage }}</p>

            <div
              v-if="offer"
              class="bg-theme-palette-emerald/10 dark:bg-theme-palette-emerald/30 p-4 rounded border border-theme-palette-emerald/40 dark:border-theme-palette-emerald"
            >
              <p class="font-medium text-theme-palette-emerald dark:text-theme-palette-emerald">
                {{
                  offer.name || $t('grant_display.upgrade_modal.recommended_offer', 'Recommended upgrade')
                }}
              </p>
              <p class="mt-1 text-sm text-theme-palette-emerald/90 dark:text-theme-palette-emerald/90">
                {{ offerTypeLabel }}
              </p>
            </div>

            <div
              v-if="missingKeys.length"
              class="bg-theme-bg-subtle dark:bg-theme-surface p-4 rounded border border-theme-border dark:border-theme-border"
            >
              <p class="font-medium mb-2 text-theme-text">
                {{ $t('grant_display.upgrade_modal.missing_keys', 'Missing access') }}
              </p>
              <ul class="flex flex-wrap gap-2 text-sm text-theme-text-secondary">
                <li v-for="key in missingKeys" :key="key">
                  <grant-display-chip :grant-key="key" />
                </li>
              </ul>
            </div>
          </div>

          <template #footer>
            <app-button-group>
              <app-button
                :label="$t('general_button_cancel', 'Cancel')"
                @click="close"
                color="white"
                outline
              />
              <app-button
                :label="ctaLabel"
                data-test="upgrade-modal-cta"
                @click="upgrade"
                color="success"
              />
            </app-button-group>
          </template>
        </card-component>
      </transition>
    </overlay>
  </transition>
</template>

<script>
import CardComponent from '@/components/CardComponent.vue';
import GrantDisplayChip from '@/components/access/GrantDisplayChip.vue';
import AppButton from '@/components/ui/buttons/AppButton.vue';
import AppButtonGroup from '@/components/ui/buttons/AppButtonGroup.vue';
import Overlay from '@/components/ui/overlays/Overlay.vue';
import { useAccess } from '@/composables/useAccess';
import { rememberSubscriptionReturnPath } from '@/utils/subscriptionCheckoutReturn';
import { mdiClose } from '@mdi/js';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

  /**
   * Global modal listening for `entitlement:missing` events dispatched
   * from the axios response interceptor (src/repositories/clients/Client.js).
   *
   * Mount once at the app root — every 403 with a structured
   * `entitlement_missing` body across the entire frontend triggers this
   * single modal. Eliminates per-route boilerplate. Missing keys are
   * collected and de-duplicated across every failing request while the modal
   * is open, so a page that loads several gated datasets shows the complete
   * missing-access set instead of whichever request failed last.
   *
   * Phase 3e: the event detail now carries scrumble's resolved
   * BillingOffer (offer_type + stripe_price_id + name + self_serve).
   * When present, the modal highlights the recommended plan name and the
   * "Upgrade" CTA copy reflects the offer_type (subscribe / contact
   * sales / etc.); the click hand-off passes the offer through to
   * useAccess.upgradeTo() so /subscriptions can deep-link to Stripe
   * Checkout when self_serve.
   */
  export default {
    name: 'UpgradeModal',
    setup() {
      const { upgradeTo } = useAccess({ refreshOnMount: false });
      const { t } = useI18n();

      const isOpen = ref(false);
      const demo = ref(false);
      const missingKeys = ref([]);
      const offer = ref(null);

      const reset = () => {
        missingKeys.value = [];
        demo.value = false;
        offer.value = null;
      };

      const handler = (event) => {
        const detail = event?.detail || {};
        demo.value = detail.demo === true;
        const incomingKeys = Array.isArray(detail.missingKeys)
          ? detail.missingKeys.map((key) => String(key || '').trim()).filter(Boolean)
          : [];
        missingKeys.value = [...new Set([...missingKeys.value, ...incomingKeys])];

        if (!offer.value && detail.offer && typeof detail.offer === 'object') {
          offer.value = detail.offer;
        }
        isOpen.value = true;
      };

      onMounted(() => {
        window.addEventListener('entitlement:missing', handler);
      });
      onBeforeUnmount(() => {
        window.removeEventListener('entitlement:missing', handler);
      });

      const close = () => {
        isOpen.value = false;
        reset();
      };

      const upgrade = () => {
        const isDemo = demo.value;
        const primary = missingKeys.value[0] ?? null;
        const resolvedOffer = offer.value;
        close();
        if (isDemo) {
          rememberSubscriptionReturnPath();
          window.location.assign('/#/subscriptions?intent=platform');
          return;
        }
        upgradeTo(primary, resolvedOffer);
      };

      const modalTitle = computed(() => {
        if (demo.value) return t('demo.paywall_title');
        if (offer.value?.offer_type === 'contact_sales') {
          return t('grant_display.upgrade_modal.contact_sales_title', 'Talk to sales');
        }
        return t('grant_display.upgrade_modal.title', 'Upgrade required');
      });

      const modalMessage = computed(() => {
        if (demo.value) return t('demo.paywall_message');
        if (offer.value?.offer_type === 'contact_sales') {
          return t(
            'grant_display.upgrade_modal.contact_sales_message',
            'This capability is available as part of a custom plan. Our team will reach out to set you up.'
          );
        }
        return t(
          'grant_display.upgrade_modal.message',
          'This feature requires a higher subscription tier.'
        );
      });

      // Raw offer_type surfaced as `data-offer-type` on the modal root so QA
      // can assert which kind of offer was presented (plan / addon /
      // token_pack / contact_sales) without matching on translated copy.
      // Empty string when the 403 carried only missing keys and no offer.
      const offerType = computed(() => offer.value?.offer_type || '');

      const offerTypeLabel = computed(() => {
        switch (offer.value?.offer_type) {
          case 'addon':
            return t('grant_display.upgrade_modal.offer_type_addon', 'Add-on for your current plan');
          case 'token_pack':
            return t('grant_display.upgrade_modal.offer_type_token_pack', 'Top-up token pack');
          case 'contact_sales':
            return t('grant_display.upgrade_modal.offer_type_contact_sales', 'Contact sales');
          case 'plan':
          default:
            return t('grant_display.upgrade_modal.offer_type_plan', 'Subscription plan');
        }
      });

      const ctaLabel = computed(() => {
        if (demo.value) return t('demo.paywall_cta');
        if (offer.value?.offer_type === 'contact_sales') {
          return t('general_button_contact_sales', 'Contact sales');
        }
        if (offer.value?.self_serve && offer.value.stripe_price_id) {
          return t('general_button_subscribe', 'Subscribe');
        }
        return t('general_button_upgrade', 'Upgrade');
      });

      return {
        isOpen,
        missingKeys,
        offer,
        modalTitle,
        modalMessage,
        offerType,
        offerTypeLabel,
        ctaLabel,
        mdiClose,
        close,
        upgrade,
      };
    },

    components: {
      Overlay,
      CardComponent,
      GrantDisplayChip,
      AppButton,
      AppButtonGroup,
    },
  };
</script>
